import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useMemo } from 'react';
import { getTestIds } from 'seagull/utils/TestIds';
import { AiWorkingOnIt } from '~/components/ai/AiWorkingOnIt/AiWorkingOnIt';
import { useLocaleKeys } from '~/i18n';
import { isCollapsibleConfirmation, selectedNumberedListOptionId, SOMETHING_ELSE_OPTION_ID } from '../AgentDraftingPane.utils';
import { AppliedMessage } from '../messages/AppliedMessage';
import { ChatTextMessage } from '../messages/ChatTextMessage';
import { ClarifyingQuestionMessage } from '../messages/ClarifyingQuestionMessage';
import { CollapsedRecap } from '../messages/CollapsedRecap/CollapsedRecap';
import { ConfirmationHierarchyMessage } from '../messages/ConfirmationHierarchyMessage';
import { ConfirmationSummaryMessage } from '../messages/ConfirmationSummaryMessage';
import { DismissedMessage } from '../messages/DismissedMessage';
import { InviteMessage } from '../messages/InviteMessage';
import { ListSelectMessage } from '../messages/ListSelectMessage';
import { ListSelectMultiMessage } from '../messages/ListSelectMultiMessage';
import { NumberedListMessage } from '../messages/NumberedListMessage/NumberedListMessage';
import { OutOfScopeMessage } from '../messages/OutOfScopeMessage';
import { RevertedMessage } from '../messages/RevertedMessage';
import { SimpleConfirmationMessage } from '../messages/SimpleConfirmationMessage';
import { UserMessage } from '../messages/UserMessage';
import { useStickyLabel } from './hooks/useStickyLabel';
import styles from './MessageThread.module.scss';
const DEFAULT_MIN_LABEL_DISPLAY_MS = 2000;
export const GetMessageThreadTID = (parentTestId)=>getTestIds(parentTestId);
const KNOWN_TOOLS = [
    'list_contacts',
    'list_service_templates',
    'list_legal_terms',
    'list_proposal_templates',
    'get_draft',
    'list_agreements',
    'get_agreement',
    'list_proposals',
    'get_proposal',
    'get_proposal_template',
    'get_contact',
    'route_request',
    'lookupBenchmarks',
    'emitPricing'
];
const isKnownTool = (tool)=>KNOWN_TOOLS.includes(tool);
const resolveFollowUpSuggestionLabel = (message, all)=>{
    var _ref;
    var _targetContent_followUp_suggestions_find;
    if (message.role !== 'user' || message.content.type !== 'apply') return null;
    const { messageId, selectedItemIds } = message.content;
    if (selectedItemIds.length !== 1) return null;
    const target = all.find((m)=>m.id === messageId);
    if (target == null || target.role !== 'assistant') return null;
    const targetContent = target.content;
    if (targetContent.type !== 'applied' && targetContent.type !== 'dismissed') return null;
    if (targetContent.followUp == null) return null;
    return (_ref = (_targetContent_followUp_suggestions_find = targetContent.followUp.suggestions.find((s)=>s.id === selectedItemIds[0])) === null || _targetContent_followUp_suggestions_find === void 0 ? void 0 : _targetContent_followUp_suggestions_find.label) !== null && _ref !== void 0 ? _ref : null;
};
// Suppressed because the highlighted option already records a something-else pick — its prompt-carried bubble would be a redundant echo.
const isNumberedListSomethingElseSelection = (message, previous)=>{
    if (message.role !== 'user' || message.content.type !== 'prompt') return false;
    if (previous == null || previous.role !== 'assistant' || previous.content.type !== 'numbered-list') return false;
    const promptText = message.content.text;
    return previous.content.options.some((option)=>option.id === SOMETHING_ELSE_OPTION_ID && option.label === promptText);
};
const synthUndoEntry = (forMessageId, undoText)=>({
        kind: 'user',
        id: "synth-undo-".concat(forMessageId),
        content: {
            type: 'prompt',
            text: undoText
        }
    });
const pendingDecisionAsMessage = (pending)=>({
        id: pending.userMessageId,
        role: 'user',
        timestamp: pending.timestamp,
        content: pending.content
    });
const mostRecentDecisionId = (localDecisions)=>{
    let latestId = null;
    let latestTimestamp = '';
    for (const [id, decision] of Object.entries(localDecisions)){
        if (decision.timestamp > latestTimestamp) {
            latestTimestamp = decision.timestamp;
            latestId = id;
        }
    }
    return latestId;
};
const buildRenderable = (messages, undoPendingAppliedId, localDecisions, undoText)=>{
    const tailIdx = messages.length - 1;
    const justResolvedId = mostRecentDecisionId(localDecisions);
    return messages.flatMap((message, idx)=>{
        if (isCollapsibleConfirmation(message)) {
            const localDecision = localDecisions[message.id];
            if (localDecision != null) {
                return [
                    {
                        kind: 'collapsedRecap',
                        id: message.id,
                        card: message,
                        userAction: pendingDecisionAsMessage(localDecision),
                        justResolved: message.id === justResolvedId
                    }
                ];
            }
            return idx === tailIdx ? [] : [
                {
                    kind: 'collapsedRecap',
                    id: message.id,
                    card: message,
                    userAction: messages[idx + 1]
                }
            ];
        }
        if (isNumberedListSomethingElseSelection(message, messages[idx - 1])) return [];
        if (message.role === 'user') {
            const followUpLabel = resolveFollowUpSuggestionLabel(message, messages);
            const content = followUpLabel != null ? {
                type: 'prompt',
                text: followUpLabel
            } : message.content;
            return [
                {
                    kind: 'user',
                    id: message.id,
                    content
                }
            ];
        }
        const assistantEntry = {
            kind: 'assistant',
            id: message.id,
            content: message.content,
            isInteractive: idx === tailIdx,
            nextMessage: messages[idx + 1]
        };
        if (message.content.type === 'reverted') {
            return [
                synthUndoEntry(message.id, undoText),
                assistantEntry
            ];
        }
        const next = messages[idx + 1];
        const nextIsReverted = next != null && next.role === 'assistant' && next.content.type === 'reverted';
        if (message.content.type === 'applied' && message.id === undoPendingAppliedId && !nextIsReverted) {
            return [
                assistantEntry,
                synthUndoEntry(message.id, undoText)
            ];
        }
        return [
            assistantEntry
        ];
    });
};
const renderAssistantContent = (content, messageId, isInteractive, nextMessage, props)=>{
    const submitFeedback = isInteractive ? (rating, comment)=>props.onSubmitFeedback(messageId, rating, comment) : undefined;
    const dismissFeedback = isInteractive ? ()=>props.onDismissFeedback(messageId) : undefined;
    switch(content.type){
        case 'invite':
            return /*#__PURE__*/ _jsx(InviteMessage, {
                content: content,
                messageId: messageId,
                onSubmitFeedback: submitFeedback,
                onDismissFeedback: dismissFeedback
            });
        case 'confirmation-summary':
            return /*#__PURE__*/ _jsx(ConfirmationSummaryMessage, {
                content: content,
                messageId: messageId,
                interactive: isInteractive,
                onDismiss: props.onDismissProposal,
                onApply: props.onApplyProposal,
                onSubmitFeedback: submitFeedback,
                onDismissFeedback: dismissFeedback
            });
        case 'confirmation-hierarchy':
            return /*#__PURE__*/ _jsx(ConfirmationHierarchyMessage, {
                content: content,
                messageId: messageId,
                interactive: isInteractive,
                onDismiss: props.onDismissProposal,
                onApply: props.onApplyProposal,
                onSubmitFeedback: submitFeedback,
                onDismissFeedback: dismissFeedback
            });
        case 'simple-confirmation':
            return /*#__PURE__*/ _jsx(SimpleConfirmationMessage, {
                content: content,
                messageId: messageId,
                interactive: isInteractive,
                onDismiss: props.onDismissProposal,
                onApply: props.onApplyProposal,
                onSubmitFeedback: submitFeedback,
                onDismissFeedback: dismissFeedback
            });
        case 'clarifying-question':
            return /*#__PURE__*/ _jsx(ClarifyingQuestionMessage, {
                content: content,
                messageId: messageId,
                onSubmitFeedback: submitFeedback,
                onDismissFeedback: dismissFeedback
            });
        case 'list-select':
            return /*#__PURE__*/ _jsx(ListSelectMessage, {
                content: content,
                messageId: messageId,
                onListSelectClick: isInteractive ? props.onListSelectClick : undefined
            });
        case 'numbered-list':
            return /*#__PURE__*/ _jsx(NumberedListMessage, {
                content: content,
                messageId: messageId,
                onOptionClick: isInteractive ? props.onNumberedListClick : undefined,
                selectedOptionId: selectedNumberedListOptionId(content, messageId, nextMessage)
            });
        case 'list-select-multi':
            return /*#__PURE__*/ _jsx(ListSelectMultiMessage, {
                content: content,
                messageId: messageId,
                interactive: isInteractive,
                onDismiss: props.onDismissProposal,
                onApply: props.onApplyProposal
            });
        case 'applied':
            return /*#__PURE__*/ _jsx(AppliedMessage, {
                content: content,
                messageId: messageId,
                showFollowUpSuggestions: isInteractive,
                onSuggestionClick: (s)=>props.onSuggestionClick(messageId, s),
                onSubmitFeedback: submitFeedback,
                onDismissFeedback: dismissFeedback,
                onUndo: isInteractive && !props.isThinking ? ()=>props.onUndo(messageId) : undefined
            });
        case 'dismissed':
            return /*#__PURE__*/ _jsx(DismissedMessage, {
                content: content,
                messageId: messageId,
                showFollowUpSuggestions: isInteractive,
                onSuggestionClick: (s)=>props.onSuggestionClick(messageId, s),
                onSubmitFeedback: submitFeedback,
                onDismissFeedback: dismissFeedback
            });
        case 'out-of-scope':
            return /*#__PURE__*/ _jsx(OutOfScopeMessage, {
                content: content,
                messageId: messageId,
                onSubmitFeedback: submitFeedback,
                onDismissFeedback: dismissFeedback
            });
        case 'chat-text':
            return /*#__PURE__*/ _jsx(ChatTextMessage, {
                content: content,
                messageId: messageId
            });
        case 'reverted':
            return /*#__PURE__*/ _jsx(RevertedMessage, {
                content: content,
                messageId: messageId
            });
    }
};
export const MessageThread = (props)=>{
    var _ref;
    var _activeToolCalls_find;
    const { messages, isThinking, activeToolCalls, thinkingText, thinkingLabel, error, minLabelDisplayMs = DEFAULT_MIN_LABEL_DISPLAY_MS, testId } = props;
    const { t } = useLocaleKeys();
    const { MessageThreadTID } = useMemo(()=>GetMessageThreadTID(testId), [
        testId
    ]);
    const activeTool = (_activeToolCalls_find = activeToolCalls.find((tc)=>tc.durationMs == null)) === null || _activeToolCalls_find === void 0 ? void 0 : _activeToolCalls_find.tool;
    const activeToolLabel = activeTool != null && isKnownTool(activeTool) ? t.agenticDrafting.messages.toolChips[activeTool]() : undefined;
    const rawLabel = (_ref = activeToolLabel !== null && activeToolLabel !== void 0 ? activeToolLabel : thinkingLabel) !== null && _ref !== void 0 ? _ref : undefined;
    const workingLabel = useStickyLabel(rawLabel, minLabelDisplayMs);
    return /*#__PURE__*/ _jsxs("div", {
        "data-testid": MessageThreadTID.Container(),
        className: styles.container,
        children: [
            buildRenderable(messages, props.undoPendingAppliedId, props.localDecisions, t.agenticDrafting.userBubbles.undo()).map((entry)=>{
                if (entry.kind === 'user') {
                    return /*#__PURE__*/ _jsx(UserMessage, {
                        content: entry.content,
                        messageId: entry.id
                    }, entry.id);
                }
                if (entry.kind === 'collapsedRecap') {
                    return /*#__PURE__*/ _jsx(CollapsedRecap, {
                        card: entry.card,
                        userAction: entry.userAction,
                        justResolved: entry.justResolved
                    }, entry.id);
                }
                return /*#__PURE__*/ _jsx("div", {
                    "data-testid": MessageThreadTID.AssistantMessage(entry.id),
                    className: styles.assistantMessage,
                    children: renderAssistantContent(entry.content, entry.id, entry.isInteractive, entry.nextMessage, props)
                }, entry.id);
            }),
            isThinking && /*#__PURE__*/ _jsx(AiWorkingOnIt, {
                label: workingLabel,
                testId: MessageThreadTID.ThinkingIndicator()
            }),
            isThinking && thinkingText != null && /*#__PURE__*/ _jsx("div", {
                "data-testid": MessageThreadTID.ThinkingLine(),
                className: styles.thinkingLine,
                children: thinkingText
            }),
            error != null && /*#__PURE__*/ _jsx("div", {
                "data-testid": MessageThreadTID.Error(),
                className: styles.error,
                children: error
            })
        ]
    });
};
